/**
 * Derives the per-user AES-256-GCM key used to encrypt the on-disk MiniSearch
 * index.
 *
 * The key is **deterministic** — derived from the current Matrix `userId`,
 * `deviceId`, and per-login search secret — so it can be re-obtained after a
 * page reload without ever being persisted anywhere. It is **non-extractable** —
 * the `CryptoKey` object cannot be serialised, logged, or exfiltrated from
 * `SubtleCrypto`.
 *
 * Why PBKDF2 when the input is already high-entropy?
 * The user-id and device-id halves of the input are semi-public (user-id is
 * readable from the IndexedDB composite key itself; device-id is queryable
 * via `POST /_matrix/client/v3/keys/query`). Stretching with 100 000 SHA-256
 * iterations (NIST SP 800-132 minimum for interactive logins) makes an
 * offline brute force infeasible even if an attacker snapshots the IndexedDB.
 *
 * The search secret is generated once per SSO login and stored in a session
 * cookie. It is stable across access-token refresh within the same login
 * session. Hashing it with SHA-256 before feeding it to PBKDF2 avoids leaking
 * the raw secret into any in-memory buffer held by `crypto.subtle.importKey`.
 * Once the secret is cleared (post-logout), the key cannot be re-derived —
 * any rows that survive a failed IndexedDB cleanup are permanently locked.
 *
 * Salt is a fixed domain-separation string, not a random salt, because we
 * need reproducibility without persistence. Any attacker trying to mount the
 * same PBKDF2 brute force would still need to know (or guess) the full
 * `userId:deviceId:SHA256(searchSecret)` triple — device ids rotate on every
 * login and search secrets are high-entropy per-login values.
 *
 * @module SearchKeyDerivation
 */

const SEARCH_KEY_SALT = 'chat-s-index-v1';
const PBKDF2_ITERATIONS = 100_000;
const AES_KEY_LENGTH_BITS = 256;

/**
 * Returns the low-level `crypto.subtle` interface if the current context
 * supports it, otherwise `null`. Search is silently disabled when this
 * returns `null` — we never fall back to unencrypted storage.
 */
export const getSubtleCrypto = (): SubtleCrypto | null => {
  if (typeof globalThis === 'undefined') return null;
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return c && c.subtle ? c.subtle : null;
};

const toHex = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
};

/**
 * Derives a non-extractable AES-256-GCM `CryptoKey` from the current user's
 * Matrix identity and per-login search secret.
 *
 * Callers must treat a `null` return as "search unavailable in this browser"
 * and hide the feature entirely. They must never fall back to unencrypted
 * storage.
 *
 * @param userId - Matrix user id, e.g. `@alice:meedio.io`
 * @param deviceId - Matrix device id, e.g. `ABCDEFGH`
 * @param searchSecret - Per-login secret from `useMxCookies().mxSearchSecret`.
 *   The secret is hashed with SHA-256 before being appended to the PBKDF2
 *   input so the raw value never appears in the imported key material.
 * @returns AES-256-GCM `CryptoKey` usable for `encrypt` + `decrypt` only, or
 *   `null` when `crypto.subtle` is unavailable or search secret is missing.
 */
export const deriveSearchKey = async (
  userId: string,
  deviceId: string,
  searchSecret: string | null | undefined
): Promise<CryptoKey | null> => {
  if (!searchSecret) return null;

  const subtle = getSubtleCrypto();
  if (!subtle) return null;

  const encoder = new TextEncoder();
  const secretDigest = await subtle.digest('SHA-256', encoder.encode(searchSecret));
  const secretHex = toHex(secretDigest);

  const keyMaterial = await subtle.importKey(
    'raw',
    encoder.encode(`${userId}:${deviceId}:${secretHex}`),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(SEARCH_KEY_SALT),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: AES_KEY_LENGTH_BITS },
    false,
    ['encrypt', 'decrypt']
  );
};
